"use client"
import { cn } from "@/lib/utils"
import Image from "next/image"
import LikeSuccessIcon from "@/public/icons/like-success-icon.svg"
import DisLikeRedIcon from "@/public/icons/dislike-red-icon.svg"
import NotSureIcon from "@/public/icons/not-sure-icon.svg"
import LikeNormalIcon from "@/public/icons/normal-like-icon.svg"
import DisLikeNormalIcon from "@/public/icons/dislike-normal-icon.svg"
import { useDateFormatter } from "@/hook/dateFormater"
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer"
import { X } from "lucide-react"
import { SingleProductEnum } from "@/constants/commerce-enum"
import type { CommentTypes } from "@/types/components/comment"
import LikeCommentServices from "@/http/api/profile/comments/LikeCommentServices"
import ReportCommentComponent from "@/components/ui/comments/report-comment-component"
import { useAuthStore } from "@/store/auth/authStore"
import WizardFormAuth from "@/components/global/modals/wizard-auth-form-modal"

const { likeComment } = new LikeCommentServices()

export default function CommentCardComponentMobile({
                                                       rate,
                                                       content,
                                                       username,
                                                       date,
                                                       drawer = false,
                                                       className,
                                                       likesCount,
                                                       dislikesCount,
                                                       id,
                                                   }: CommentTypes) {
    const formatDate = useDateFormatter()
    const formattedDate = formatDate(date)
    const { token } = useAuthStore((state) => state)

    const likeCommentHandler = async (state: "LIKED" | "DISLIKED") => {
        const payload = {
            state,
            comment_id: id,
        }
        await likeComment(payload)
    }

    const ratingClass = cn("w-[35px] h-[20px] text-white flex items-center justify-center rounded-md", {
        "bg-green-500": rate > 3,
        "bg-red-500": rate < 3,
        "bg-yellow-400": rate === 3,
    })

    const renderRatingContent = () => (
        <>
            {rate > 3 && (
                <>
                    <Image src={LikeSuccessIcon || "/placeholder.svg"} alt="LikeSuccessIcon" height={18} width={18} />
                    <h4 className="flex text-green-500 flex-wrap text-xs font-medium">{SingleProductEnum.recommendTOBuy}</h4>
                </>
            )}
            {rate < 3 && (
                <>
                    <Image src={DisLikeRedIcon || "/placeholder.svg"} alt="DisLikeRedIcon" height={18} width={18} />
                    <h4 className="flex text-red-500 flex-wrap text-xs font-medium">{SingleProductEnum.notRecommendTOBuy}</h4>
                </>
            )}
            {rate === 3 && (
                <>
                    <Image src={NotSureIcon || "/placeholder.svg"} alt="NotSureIcon" height={18} width={18} />
                    <h4 className="flex text-yellow-400 flex-wrap text-xs font-medium">{SingleProductEnum.notSure}</h4>
                </>
            )}
        </>
    )

    const renderLikeButtons = () => (
        <div className="flex gap-3 flex-wrap items-center w-full justify-end">
            {token ? (
                <div
                    onClick={() => likeCommentHandler("LIKED")}
                    className="flex gap-1 p-1 !text-base items-center justify-start cursor-pointer"
                >
                    {likesCount?.toLocaleString("fa")}
                    <Image src={LikeNormalIcon || "/placeholder.svg"} width={18} height={18} alt="LikeNormalIcon" />
                </div>
            ) : (
                <WizardFormAuth
                    customButton={
                        <div className="flex gap-1 p-1 !text-base items-center justify-start cursor-pointer">
                            {likesCount?.toLocaleString("fa")}
                            <Image src={LikeNormalIcon || "/placeholder.svg"} width={18} height={18} alt="LikeNormalIcon" />
                        </div>
                    }
                />
            )}

            {token ? (
                <div
                    onClick={() => likeCommentHandler("DISLIKED")}
                    className="flex p-1 gap-1 !text-base items-center justify-start cursor-pointer"
                >
                    {dislikesCount?.toLocaleString("fa") || "0"}
                    <Image src={DisLikeNormalIcon || "/placeholder.svg"} width={18} height={18} alt="DisLikeNormalIcon" />
                </div>
            ) : (
                <WizardFormAuth
                    customButton={
                        <div className="flex p-1 gap-1 !text-base items-center justify-start cursor-pointer">
                            {dislikesCount?.toLocaleString("fa") || "0"}
                            <Image src={DisLikeNormalIcon || "/placeholder.svg"} width={18} height={18} alt="DisLikeNormalIcon" />
                        </div>
                    }
                />
            )}
        </div>
    )

    const renderCommentContent = (isInDrawer = false) => (
        <div
            className={cn(
                "w-full flex-shrink-0 rounded-2xl p-2.5",
                className,
                { border: !drawer || isInDrawer },
                { "h-full": !isInDrawer },
                { "pb-4": !isInDrawer },
                { "pb-20": isInDrawer },
            )}
        >
            <div className="w-full flex content-between h-full flex-wrap">
                <div className="w-full text-xs gap-1 flex flex-wrap items-center justify-between">
                    <span className={ratingClass}>{rate.toLocaleString("fa")}</span>
                    <div className="flex-1 h-full flex items-center justify-between px-2 gap-1">
                        <div className={"flex items-center h-full gap-2"}>
                            <div className="text-gray-400 truncate">{username}</div>
                            <div className="bg-gray-200 mx-1 h-[20px] w-[1px]"></div>
                            <span className="text-gray-400">{formattedDate}</span>
                        </div>
                        <span>
              <ReportCommentComponent id={id} agent={"mobile"} />
            </span>
                    </div>
                </div>

                <div className="w-full flex items-end flex-wrap py-3 gap-2">{renderRatingContent()}</div>

                <div className={`w-full text-sm ${!isInDrawer && drawer ? "line-clamp-2" : ""} text-start leading-normal`}>
                    {content}
                </div>

                <div className="w-full items-center justify-end mt-1">{renderLikeButtons()}</div>
            </div>
        </div>
    )

    if (drawer) {
        const shouldUseDrawer = content.length > 15

        return (
            <Drawer>
                {shouldUseDrawer ? (
                    <DrawerTrigger asChild>
                        <div className="w-[258px] flex-shrink-0 h-[213px] border rounded-2xl p-1 cursor-pointer">
                            {renderCommentContent(false)}
                        </div>
                    </DrawerTrigger>
                ) : (
                    <div className="w-[258px] flex-shrink-0 h-[213px] border rounded-2xl p-1">{renderCommentContent(false)}</div>
                )}

                <DrawerContent>
                    <DrawerHeader>
                        <DrawerTitle>
                            <div className="w-full items-center justify-between border-b p-3 flex flex-wrap">
                                <h6>{SingleProductEnum.commentDetails}</h6>
                                <DrawerClose>
                                    <X className="w-6 h-6" />
                                </DrawerClose>
                            </div>
                        </DrawerTitle>
                        {renderCommentContent(true)}
                    </DrawerHeader>
                </DrawerContent>
            </Drawer>
        )
    }

    return renderCommentContent(false)
}

